import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Card, Input } from '../../components/primitives/index.js'
import { useCustomer } from '../../context/CustomerContext.jsx'
import { useProducts } from '../../context/ProductsContext.jsx'
import { customerApi } from '../../lib/customerApi.js'
// The same module the API validates with, so the form and the server can
// never disagree about who is old enough.
import { validateDateOfBirth, latestAllowedDob, MIN_AGE_YEARS } from '../../../worker/src/age.js'
import './AccountSettings.css'

export default function AccountSettings() {
  const { customer, refresh, logout } = useCustomer()
  const { checkoutConfig } = useProducts()
  const storeName = checkoutConfig?.company?.name?.trim()
  const navigate = useNavigate()
  const [profile, setProfile] = useState({
    name: customer?.name ?? '',
    phone: customer?.phone ?? '',
    discord: customer?.discord ?? '',
    birthday: customer?.birthday ?? '',
    marketingOptIn: customer?.marketingOptIn ?? false,
  })
  const [savingProfile, setSavingProfile] = useState(false)
  const [profileMsg, setProfileMsg] = useState('')
  const [profileError, setProfileError] = useState('')
  // Kept apart from profileError: the date field renders its own message
  // inline, and sharing one string showed it twice.
  const [dobError, setDobError] = useState('')

  const hasPassword = customer?.hasPassword ?? true
  const [password, setPassword] = useState({ current: '', next: '', confirm: '' })
  const [savingPassword, setSavingPassword] = useState(false)
  const [passwordMsg, setPasswordMsg] = useState('')
  const [passwordError, setPasswordError] = useState('')

  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [deletePassword, setDeletePassword] = useState('')
  const [deleteError, setDeleteError] = useState('')
  const [deleting, setDeleting] = useState(false)

  async function handleProfileSubmit(e) {
    e.preventDefault()
    setProfileMsg('')
    setProfileError('')
    setDobError('')

    const dob = validateDateOfBirth(profile.birthday)
    if (!dob.ok) {
      // Branded here rather than in the API, which serves whatever store is
      // deployed on it and has no business hard-coding a name.
      setDobError(dob.age !== undefined && storeName
        ? `You must be at least ${MIN_AGE_YEARS} years old to use ${storeName}.`
        : dob.error)
      return
    }

    setSavingProfile(true)
    try {
      await customerApi.updateMe(profile)
      await refresh()
      setProfileMsg('Profile updated.')
    } catch (err) {
      // Previously this had no catch at all, so anything the API rejected
      // vanished and the form just sat there.
      setProfileError(err.message ?? 'Could not save your profile. Please try again.')
    } finally {
      setSavingProfile(false)
    }
  }

  async function handlePasswordSubmit(e) {
    e.preventDefault()
    setPasswordError('')
    setPasswordMsg('')
    if (hasPassword && !password.current) {
      setPasswordError('Enter your current password.')
      return
    }
    if (password.next.length < 8) {
      setPasswordError('New password must be at least 8 characters.')
      return
    }
    if (password.next !== password.confirm) {
      setPasswordError('Passwords do not match.')
      return
    }
    setSavingPassword(true)
    try {
      await customerApi.updateMe({ currentPassword: password.current, newPassword: password.next })
      setPasswordMsg(hasPassword ? 'Password updated.' : 'Password set.')
      setPassword({ current: '', next: '', confirm: '' })
      await refresh()
    } catch (err) {
      setPasswordError(err.message ?? 'Could not update password.')
    } finally {
      setSavingPassword(false)
    }
  }

  async function handleDelete() {
    setDeleteError('')
    setDeleting(true)
    try {
      await customerApi.deleteAccount({ currentPassword: deletePassword })
      await logout()
      navigate('/')
    } catch (err) {
      setDeleteError(err.message ?? 'Could not delete account.')
      setDeleting(false)
    }
  }

  return (
    <div className="account-settings">
      <Card>
        <h3>Profile</h3>
        {/* noValidate so our own message wins. `max` on the date field still
            constrains the browser's picker, but without this the browser
            also blocks the submit with a terse native bubble and
            handleProfileSubmit never runs — the clear, branded explanation
            would never be seen. */}
        <form className="account-settings__form" noValidate onSubmit={handleProfileSubmit}>
          <Input label="Name" value={profile.name} onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))} />
          <Input label="Email" type="email" value={customer?.email ?? ''} disabled />
          <Input label="Phone" type="tel" placeholder="(555) 555-5555" value={profile.phone} onChange={(e) => setProfile((p) => ({ ...p, phone: e.target.value }))} />
          <Input label="Discord username" placeholder="username" value={profile.discord} onChange={(e) => setProfile((p) => ({ ...p, discord: e.target.value }))} />
          <Input
            label="Date of birth"
            type="date"
            value={profile.birthday ?? ''}
            // Stops the browser's own picker from offering an under-age date,
            // which is a kinder no than typing one and being told off.
            max={latestAllowedDob()}
            error={dobError || undefined}
            onChange={(e) => { setProfile((p) => ({ ...p, birthday: e.target.value })); setDobError('') }}
          />
          {/* Stood down while the error is up: the two say the same thing,
              and the error says it better. */}
          {!dobError && (
            <p className="account-settings__hint">
              You must be {MIN_AGE_YEARS} or over to hold an account. Adding your date of birth also earns you a
              birthday bonus each year.
            </p>
          )}
          <label className="account-settings__checkbox">
            <input
              type="checkbox"
              checked={profile.marketingOptIn}
              onChange={(e) => setProfile((p) => ({ ...p, marketingOptIn: e.target.checked }))}
            />
            Email me about new products, sales, and promotions
          </label>
          {profileError && <p className="account-settings__error">{profileError}</p>}
          {profileMsg && <p className="account-settings__msg">{profileMsg}</p>}
          <Button type="submit" variant="primary" loading={savingProfile}>
            Save profile
          </Button>
        </form>
      </Card>

      <Card>
        <h3>Password</h3>
        <form className="account-settings__form" onSubmit={handlePasswordSubmit}>
          {hasPassword && (
            <Input label="Current password" type="password" required value={password.current} onChange={(e) => setPassword((p) => ({ ...p, current: e.target.value }))} />
          )}
          <Input label="New password" type="password" required value={password.next} onChange={(e) => setPassword((p) => ({ ...p, next: e.target.value }))} />
          <Input label="Confirm new password" type="password" required value={password.confirm} onChange={(e) => setPassword((p) => ({ ...p, confirm: e.target.value }))} error={passwordError} />
          {passwordMsg && <p className="account-settings__msg">{passwordMsg}</p>}
          <Button type="submit" variant="primary" loading={savingPassword}>
            {hasPassword ? 'Update password' : 'Set password'}
          </Button>
        </form>
      </Card>

      <Card className="account-settings__danger">
        <h3>Delete account</h3>
        <p>This permanently removes your account, saved addresses, and order history.</p>
        {!confirmingDelete ? (
          <Button variant="ghost" onClick={() => setConfirmingDelete(true)}>
            Delete my account
          </Button>
        ) : (
          <div className="account-settings__form">
            {hasPassword && (
              <Input
                label="Enter your password to confirm"
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                error={deleteError}
              />
            )}
            <div className="account-settings__confirm">
              <Button variant="ghost" onClick={() => { setConfirmingDelete(false); setDeletePassword(''); setDeleteError('') }}>
                Cancel
              </Button>
              <Button variant="primary" loading={deleting} disabled={hasPassword && !deletePassword} onClick={handleDelete}>
                Yes, delete permanently
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}
